/**
 * "Is this the paragraph the preview was built from?" — the single predicate
 * behind both the preview's stale guard and the pre-delete verification.
 *
 * It lives in one place on purpose: the two callers must not be able to disagree
 * about what counts as the same paragraph, or a card could preview as applicable
 * and then act on something else.
 *
 * The two callers differ only in whether they GATE the call, and that difference
 * is deliberate:
 *  - the preview skips the check entirely when the BE sent no snapshot, which is
 *    what it has always done;
 *  - the delete path always calls, because an unverifiable destructive operation
 *    must not run on a guess.
 */

import { EditOperationType } from "../../../interfaces/autoRedlineTypes";
import { normalizeParagraphText } from "../../../helpers/word";
import { getAppliedParagraphText } from "./appliedParagraphLedger";

export type ParagraphMismatchReason = "out_of_bounds" | "text_changed";

export interface ParagraphIdentityInput {
  /** The BE's snapshot of the target paragraph, raw and un-normalized. */
  expectedText: string | undefined;

  /** Live `paragraph.text` read back from Word, raw. */
  liveText: string;

  /** True when the tracker-adjusted index fell outside the document. */
  outOfBounds?: boolean;

  /** Self-attribution is only sound for index-targeted operations. */
  operationType: EditOperationType;

  /** Ledger coordinates. Omit messageId to refuse self-attribution. */
  messageId?: string;
  paragraphId: number;
}

export interface ParagraphIdentityResult {
  matched: boolean;
  /** True when the only difference is a change WE applied earlier this message. */
  selfAttributed: boolean;
  reason?: ParagraphMismatchReason;
}

/**
 * Decide whether the live paragraph is the one the preview was generated
 * against. Order matters — see the inline notes.
 */
export function matchesExpectedParagraph(input: ParagraphIdentityInput): ParagraphIdentityResult {
  const { expectedText, liveText, outOfBounds, operationType, messageId, paragraphId } = input;

  // Checked first so a ledger entry can never rescue an index that has already
  // fallen off the end of the document.
  if (outOfBounds) {
    return { matched: false, selfAttributed: false, reason: "out_of_bounds" };
  }

  const expected = normalizeParagraphText(expectedText ?? "");
  const live = normalizeParagraphText(liveText);

  // Deliberately includes "" === "": deleting a blank paragraph whose snapshot
  // is also blank is a legitimate, verifiable operation.
  if (expected === live) {
    return { matched: true, selfAttributed: false };
  }

  // Self-attribution: the text differs because a sibling card in THIS message
  // already edited this paragraph. INSERT/DELETE resolve their target by index
  // and never read its text, so our own edit does not invalidate them.
  //
  // EDIT is excluded — its searchPhrase occurrence ordinals were counted against
  // the pre-edit snapshot, so a second edit on an already-edited paragraph is
  // genuinely unsafe.
  //
  // The non-empty check is defence in depth: normalizeParagraphText collapses
  // "", "\r" and "   " alike, so an empty ledger value would match any blank
  // paragraph. (The ledger already refuses to store one.)
  const indexTargeted = operationType === "insert" || operationType === "delete";
  if (indexTargeted && messageId) {
    const appliedText = getAppliedParagraphText(messageId, paragraphId);
    if (appliedText && appliedText === live) {
      return { matched: true, selfAttributed: true };
    }
  }

  return { matched: false, selfAttributed: false, reason: "text_changed" };
}
